import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Loader } from '../../components/loader';
import supabase from '../../supabase';
import { ArrowLeft } from 'lucide-react';
import { getErrorMessage } from '../../helpers/get-error-message';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tokenProcessed, setTokenProcessed] = useState(false);

  // Check if we have access to reset password
  useEffect(() => {
    const checkAccess = async () => {
      // Don't process token multiple times
      if (tokenProcessed) return;

      // Get URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const tokenHash = urlParams.get('token_hash');
      const type = urlParams.get('type');

      // If we have a reset token, verify it with Supabase
      if (tokenHash && type === 'recovery') {
        try {
          setTokenProcessed(true);

          // For password recovery, we need to use verifyOtp with the token_hash
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });

          if (error) {
            setError(
              'Invalid or expired reset link. Please request a new password reset.',
            );
            return;
          }

          // verifyOtp should establish the session automatically
          if (data.user && data.session) {
            // Force a session refresh to make sure it's properly set
            await supabase.auth.getSession();
          } else {
            setError('Failed to establish session with reset token.');
          }
        } catch (err) {
          setError(
            'Invalid or expired reset link. Please request a new password reset.',
          );
        }
      } else {
        // No token found, check if user has an existing session
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) {
          setError(
            'No valid reset token found. Please request a new password reset.',
          );
        }
      }
    };
    checkAccess();
  }, [navigate, tokenProcessed]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      // Check current session before updating password
      const { data: sessionData } = await supabase.auth.getSession();

      if (!sessionData.session) {
        setError(
          'No active session. Please try clicking the reset link again.',
        );
        setIsLoading(false);
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) throw error;
      setIsSuccess(true);
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to reset password'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex flex-col min-h-screen bg-bg-main">
        <div className="flex items-center p-4">
          <button
            onClick={() => navigate('/sign-in')}
            className="hover:text-accent-purple transition-colors absolute left-4"
          >
            <ArrowLeft className="h-5 w-5 text-title" />
          </button>
          <h1 className="text-lg font-medium text-center flex-1 text-title">
            Password Reset
          </h1>
        </div>

        <div className="flex justify-center my-6">
          <div className="w-16 h-16">
            <img
              src="/images/llaami.svg"
              alt="Llaami Logo"
              className="w-full h-full"
            />
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-sm space-y-6">
            <div className="space-y-2 text-center">
              <p className="text-subheading">
                Your password has been reset successfully. You can now sign in
                with your new password.
              </p>
            </div>
            <Button
              onClick={() => navigate('/sign-in')}
              className="w-full bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center h-12"
            >
              Sign In
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-bg-main">
      <div className="flex items-center p-4">
        <button
          onClick={() => navigate('/sign-in')}
          className="hover:text-accent-purple transition-colors absolute left-4"
        >
          <ArrowLeft className="h-5 w-5 text-title" />
        </button>
        <h1 className="text-lg font-medium text-center flex-1 text-title">
          Reset Password
        </h1>
      </div>

      <div className="flex justify-center my-6">
        <div className="w-16 h-16">
          <img
            src="/images/llaami.svg"
            alt="Llaami Logo"
            className="w-full h-full"
          />
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-6">
          <div className="space-y-2 text-center">
            <p className="text-subheading">Enter your new password below.</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Input
                id="password"
                placeholder="New Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                disabled={isLoading}
                className="h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                style={{ fontSize: '16px' }}
              />
              <Input
                id="confirm-password"
                placeholder="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
                disabled={isLoading}
                className="h-12 bg-white border-outline focus:border-accent-purple focus:ring-accent-purple focus:outline-none"
                style={{ fontSize: '16px' }}
              />
              {error && (
                <p className="text-sm text-incorrect text-center">{error}</p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full bg-accent-purple hover:bg-accent-purple/90 text-white rounded-full py-6 flex items-center justify-center h-12"
              disabled={isLoading || !password || !confirmPassword}
            >
              {isLoading ? <Loader /> : 'Reset Password'}
            </Button>
          </form>

          <div className="text-center">
            <button
              onClick={() => navigate('/sign-in')}
              className="text-accent-purple hover:text-accent-purple/80"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
